// Highlights, inside a textarea, the words a searcher won't see: a backdrop with the same
// text and metrics sits behind the (transparent) textarea and tints the hidden ranges.
import { keptEnd, type Config, type FieldResult } from '../engine';
import { h } from './dom';

export interface Highlighter {
  el: HTMLElement;
  /** Tint the hidden parts of `f` (or clear when undefined). Returns the note text parts. */
  update(f: FieldResult | undefined): { limit: string; google: string };
}

/** Map offsets in normalize(raw) back to offsets in raw (spec §4.2 normalization). */
export function normToRaw(raw: string): number[] {
  const map: number[] = [];
  const ws = /[ \t\n\r\f]/;
  let inRun = false;
  let started = false;
  for (let i = 0; i < raw.length; i++) {
    if (ws.test(raw[i])) {
      if (started && !inRun) { map.push(i); inRun = true; }
    } else {
      map.push(i);
      started = true;
      inRun = false;
    }
  }
  if (inRun) map.pop(); // trailing whitespace is trimmed
  map.push(raw.length); // end sentinel
  return map;
}

export function highlighter(input: HTMLTextAreaElement, config: () => Config): Highlighter {
  const backdrop = h('div', { class: 'hl-backdrop', 'aria-hidden': 'true' });
  const el = h('div', { class: 'hl-wrap' }, backdrop, input);
  input.classList.add('hl-input');
  input.addEventListener('scroll', () => { backdrop.scrollTop = input.scrollTop; });
  // Grow to fit the text, so the field never scrolls and the backdrop stays aligned.
  const fit = () => {
    if (!input.isConnected) return;
    input.style.height = 'auto';
    input.style.height = `${input.scrollHeight + 2}px`;
  };
  input.addEventListener('input', fit);
  // Refit only when the width changes, and outside the observer callback: resizing the
  // observed box from inside it triggers a "ResizeObserver loop" error in WebKit.
  let lastWidth = -1;
  new ResizeObserver(entries => {
    const w = entries[0]?.contentRect.width ?? 0;
    if (w === lastWidth) return;
    lastWidth = w;
    requestAnimationFrame(fit);
  }).observe(el);

  function update(f: FieldResult | undefined) {
    const raw = input.value;
    backdrop.textContent = '';
    const empty = { limit: '', google: '' };
    if (!f || f.overall === 'error' || !f.results.length) {
      backdrop.append(raw + '\n');
      return empty;
    }
    // Earliest cut across enabled devices; Google's cut is the same everywhere.
    const shownEnd = Math.min(...f.results.map(r => r.shownEnd));
    const kept = Math.max(shownEnd, keptEnd(f, config()));
    const map = normToRaw(raw);
    const at = (n: number) => map[Math.min(n, map.length - 1)];
    // Start highlights at the next word, not on the space before it.
    const skip = (n: number) => { while (n < f.original.length && f.original[n] === ' ') n++; return n; };
    const a = at(skip(shownEnd)), b = at(kept), c = at(skip(kept)), end = raw.length;
    backdrop.append(raw.slice(0, a));
    if (b > a) backdrop.append(h('mark', { class: 'hl-limit' }, raw.slice(a, b)));
    if (c > b) backdrop.append(raw.slice(b, c));
    if (end > c) backdrop.append(h('mark', { class: 'hl-google' }, raw.slice(c, end)));
    backdrop.append('\n');
    fit();
    backdrop.scrollTop = input.scrollTop;
    return { limit: f.original.slice(shownEnd, kept).trim(), google: f.original.slice(kept).trim() };
  }

  return { el, update };
}
